import { taskCategoryLabel } from '../../lib/taskProcess';
import type { QuarterReview, SessionUser, TaskRecord } from '../../types';

interface ReviewTabProps {
    sessionUser: SessionUser;
    visibleTasks: TaskRecord[];
    handleQuantitativeChange: (taskId: string, quarter: string, rateValue: string) => void;
    handleQualitativeCheckToggle: (taskId: string, quarter: string, field: string) => void;
    handleConfirmQuarter: (taskId: string, quarter: string) => void;
    handleUnlockQuarter: (taskId: string, quarter: string) => void;
}

export function ReviewTab({
    sessionUser,
    visibleTasks,
    handleQuantitativeChange,
    handleQualitativeCheckToggle,
    handleConfirmQuarter,
    handleUnlockQuarter,
}: ReviewTabProps) {
    return (
        <div>
            <div className="mb-6">
                <h2 className="text-lg font-bold text-slate-900">상시 실적 기반 분기 평가 · 파트장 Confirm</h2>
                <p className="text-sm text-slate-500">Assign 승인된 과제만 분기 입력 가능 · 분기별 Confirm 후 잠금</p>
            </div>

            {visibleTasks.length === 0 && (
                <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-400 text-sm">
                    등록된 과제가 없습니다. 구성원이 과제를 등록하면 이곳에서 분기 실적을 관리할 수 있습니다.
                </div>
            )}

            <div className="grid grid-cols-1 gap-8">
                {visibleTasks.map(task => {
                    const reviews = Object.values(task.quarterReviews || {}) as QuarterReview[];
                    const avgScore = reviews.length ? reviews.reduce((acc, curr) => acc + (curr.score || 0), 0) / reviews.length : 0;
                    const isApproved = task.status === 'APPROVED';
                    const canEditQuarter = sessionUser.role === 'EVALUATOR' && isApproved;

                    let recommendedGrade = 'GD';
                    let gradeBg = 'bg-slate-100 text-slate-800';
                    if (avgScore >= 100) {
                        recommendedGrade = 'EX';
                        gradeBg = 'bg-indigo-600 text-white';
                    } else if (avgScore < 85) {
                        recommendedGrade = 'NI';
                        gradeBg = 'bg-rose-100 text-rose-700';
                    }

                    return (
                        <div key={task.id} className={`bg-white rounded-xl border p-6 shadow-sm ${isApproved ? 'border-slate-200' : 'border-slate-200 opacity-80'}`}>
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 pb-4 border-b border-slate-100">
                                <div>
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <span className={`text-xs px-2.5 py-1 rounded-full font-bold inline-block ${task.type === 'QUANTITATIVE' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                                            {task.type === 'QUANTITATIVE' ? '정량 업무' : '정성 업무'}
                                        </span>
                                        <span className="text-xs px-2.5 py-1 rounded-full font-bold bg-violet-50 text-violet-700">
                                            {taskCategoryLabel(task.taskCategory)}
                                        </span>
                                        <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${isApproved ? 'bg-indigo-50 text-indigo-700' : task.status === 'REJECTED' ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>
                                            {isApproved ? 'Assign 승인' : task.status === 'REJECTED' ? '반려' : 'Assign 대기'}
                                        </span>
                                        {task.ownerName && (
                                            <span className="text-xs px-2.5 py-1 rounded-full font-bold bg-slate-900 text-white">
                                                {task.ownerName}
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="text-base font-bold text-slate-900">{task.title}</h3>
                                    {!isApproved && (
                                        <p className="text-xs text-amber-700 mt-1">과제 Assign(승인) 완료 후 분기 실적 입력·Confirm이 가능합니다.</p>
                                    )}
                                </div>

                                <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200 w-full md:w-auto justify-between">
                                    <div className="text-right">
                                        <span className="text-xs text-slate-400 block">연간 환산 평균</span>
                                        <span className="text-lg font-bold text-indigo-600">{avgScore.toFixed(1)}점</span>
                                    </div>
                                    <div className={`px-4 py-2 rounded-lg font-bold text-center text-sm ${gradeBg}`}>
                                        <span className="text-[10px] block opacity-75">추천 등급</span>
                                        {recommendedGrade}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                {['Q1', 'Q2', 'Q3', 'Q4'].map(quarter => {
                                    const qReview = (task.quarterReviews || {})[quarter] || { rating: 'POOR', score: 80, rate: 0, checks: {}, confirmed: false };
                                    const quarterLocked = !!qReview.confirmed;
                                    const inputsDisabled = !canEditQuarter || quarterLocked;
                                    return (
                                        <div key={quarter} className={`rounded-xl p-4 border relative ${quarterLocked ? 'bg-indigo-50/40 border-indigo-200' : 'bg-slate-50 border-slate-200'}`}>
                                            <div className="flex justify-between items-center mb-3">
                                                <span className="font-bold text-slate-900 text-sm">{quarter} 평가</span>
                                                <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                                                    qReview.rating === 'EXCELLENT' ? 'bg-indigo-100 text-indigo-700' :
                                                    qReview.rating === 'GOOD' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'
                                                }`}>
                                                    {qReview.rating === 'EXCELLENT' ? '훌륭해요 (110)' :
                                                     qReview.rating === 'GOOD' ? '준수해요 (90)' : '노력해요 (80)'}
                                                </span>
                                            </div>

                                            {task.type === 'QUANTITATIVE' ? (
                                                <div className="space-y-2">
                                                    <label className="text-xs text-slate-500 block">실적 달성률 (%)</label>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={qReview.rate || 0}
                                                            onChange={(e) => handleQuantitativeChange(task.id, quarter, e.target.value)}
                                                            disabled={inputsDisabled}
                                                            className="bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-full font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100"
                                                        />
                                                        <span className="text-sm font-bold text-slate-600">%</span>
                                                    </div>
                                                    <span className="text-[10px] text-slate-400 block leading-tight">
                                                        * 100% 초과: 훌륭 / 85%~100%: 준수 / 85% 미만: 노력
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    <label className="text-xs text-slate-500 block mb-1">성과 측정 4대 지표</label>
                                                    <div className="space-y-1">
                                                        {[
                                                            { key: 'suitability', label: '성과 적정성' },
                                                            { key: 'timeliness', label: '수행 적시성' },
                                                            { key: 'collaboration', label: '협업 만족도' },
                                                            { key: 'effort', label: '노력/기여도' }
                                                        ].map(indicator => (
                                                            <label key={indicator.key} className={`flex items-center gap-2 text-xs text-slate-700 ${inputsDisabled ? 'opacity-60' : 'cursor-pointer hover:text-slate-900'}`}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={(qReview.checks || {})[indicator.key] || false}
                                                                    onChange={() => handleQualitativeCheckToggle(task.id, quarter, indicator.key)}
                                                                    disabled={inputsDisabled}
                                                                    className="rounded text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                                                                />
                                                                <span>{indicator.label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                    <span className="text-[10px] text-slate-400 block leading-tight mt-1">
                                                        * YES 3개 이상: 훌륭 / 2개: 준수 / 1개 이하: 노력
                                                    </span>
                                                </div>
                                            )}

                                            {sessionUser.role === 'EVALUATOR' && isApproved && (
                                                <div className="mt-3 pt-3 border-t border-slate-200/80">
                                                    {quarterLocked ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleUnlockQuarter(task.id, quarter)}
                                                            className="w-full text-[11px] font-bold text-slate-600 bg-white border border-slate-300 rounded-lg py-1.5 hover:bg-slate-50"
                                                        >
                                                            Confirm 해제
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleConfirmQuarter(task.id, quarter)}
                                                            className="w-full text-[11px] font-bold text-indigo-700 bg-indigo-100 border border-indigo-200 rounded-lg py-1.5 hover:bg-indigo-200"
                                                        >
                                                            파트장 Confirm
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            {quarterLocked && (
                                                <p className="text-[10px] text-indigo-600 font-semibold mt-2 text-center">확정됨 · 수정 잠금</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
